import {
  Badge,
  Button,
  confirm,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormNumber,
  FormRoot,
  FormTextarea,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import {
  CalendarDaysIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UserPlusIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";
import { toDateTimeLocalValue } from "@/shared/lib/datetime-local-form";
import { formatDateTimeLocal } from "@/shared/lib/date-time-performance";
import { FormDateQuarterTimeField } from "@/shared/ui/FormDateQuarterTimeField";
import type {
  CreateEventShowDTO,
  EventShowDTO,
  UpdateEventShowDTO,
} from "../../domain/contracts";
import { summarizeEventShowSchedule } from "../../domain/services/event-show-schedule-ui";
import {
  useAddEventShowToAllAssignments,
  useCreateEventShow,
  useDeleteEventShow,
  useEventShowsQuery,
  useUpdateEventShow,
} from "../api/queries";
import { performanceApi } from "../api/client";

const EventShowFormSchema = z.object({
  showDate: z.string().min(1, "ຕ້ອງລະບຸວັນໂຊ"),
  sortOrder: z.number().int(),
  notes: z.string().nullable().optional(),
});

type EventShowFormValues = z.infer<typeof EventShowFormSchema>;

type DialogState =
  | { open: false }
  | { open: true; mode: "create" }
  | { open: true; mode: "edit"; show: EventShowDTO };

type EventShowsScheduleProps = {
  eventId: string;
  canManage: boolean;
};

function EventShowDialog({
  state,
  submitting,
  onOpenChange,
  onSubmitCreate,
  onSubmitEdit,
  onDelete,
}: {
  state: DialogState;
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitCreate: (input: CreateEventShowDTO) => void | Promise<void>;
  onSubmitEdit: (
    showId: string,
    input: UpdateEventShowDTO,
  ) => void | Promise<void>;
  onDelete?: (showId: string) => void | Promise<void>;
}) {
  const open = state.open;
  const mode = state.open ? state.mode : "create";
  const editing = state.open && state.mode === "edit" ? state.show : null;

  const methods = RHF.useForm<EventShowFormValues>({
    resolver: zodResolver(EventShowFormSchema),
    values: {
      showDate: editing
        ? toDateTimeLocalValue(editing.showDate)
        : toDateTimeLocalValue(new Date()),
      sortOrder: editing?.sortOrder ?? 0,
      notes: editing?.notes ?? null,
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "ແກ້ໄຂໂຊ" : "ເພີ່ມໂຊ"}</DialogTitle>
        </DialogHeader>
        <FormRoot
          key={editing?.id ?? "create"}
          methods={methods}
          onSubmit={async (vals) => {
            if (mode === "edit" && editing) {
              await onSubmitEdit(editing.id, {
                showDate: vals.showDate,
                sortOrder: vals.sortOrder,
                notes: vals.notes?.trim() ? vals.notes.trim() : null,
              });
            } else {
              await onSubmitCreate({
                showDate: vals.showDate,
                notes: vals.notes?.trim() ? vals.notes.trim() : null,
              });
            }
            onOpenChange(false);
          }}
          className="space-y-4"
        >
          <FormDateQuarterTimeField
            name="showDate"
            label="ວັນ-ເວລາໂຊ"
            requiredMark
          />
          {mode === "edit" ? (
            <FormNumber name="sortOrder" label="ລຳດັບ" min={0} />
          ) : null}
          <FormTextarea
            name="notes"
            label="ໝາຍເຫດ"
            rows={2}
            placeholder="ບໍ່ບັງຄັບ"
          />
          <DialogFooter className="gap-2 sm:justify-between">
            {mode === "edit" && editing && onDelete ? (
              <Button
                type="button"
                variant="outline"
                className="text-destructive"
                disabled={submitting}
                onClick={async () => {
                  await onDelete(editing.id);
                  onOpenChange(false);
                }}
              >
                <Trash2Icon className="size-4" />
                ລຶບ
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
              </Button>
            </div>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}

export function EventShowsSchedule({
  eventId,
  canManage,
}: EventShowsScheduleProps) {
  const showsQuery = useEventShowsQuery(eventId);
  const createShow = useCreateEventShow(eventId);
  const updateShow = useUpdateEventShow(eventId);
  const deleteShow = useDeleteEventShow(eventId);
  const addShowToAll = useAddEventShowToAllAssignments(eventId);

  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const [lastCreatedShowId, setLastCreatedShowId] = useState<string | null>(
    null,
  );

  const shows = showsQuery.data ?? [];
  const summary = useMemo(() => summarizeEventShowSchedule(shows), [shows]);
  const submitting =
    createShow.isPending ||
    updateShow.isPending ||
    deleteShow.isPending ||
    addShowToAll.isPending;

  const confirmDeleteShow = async (showId: string) => {
    try {
      const impact = await performanceApi.events.shows.deleteImpact(
        eventId,
        showId,
      );
      const show = shows.find((item) => item.id === showId);
      const showLabel = show
        ? formatDateTimeLocal(show.showDate)
        : "ໂຊນີ້";

      const description =
        impact.blockedAssignmentCount > 0
          ? `ບໍ່ສາມາດລຶບໄດ້ — ມີ ${impact.blockedAssignmentCount} assignment ທີ່ເລືອກໂຊນີ້ເປັນໂຊສຸດທ້າຍ`
          : impact.linkedAssignmentCount > 0
            ? `ມີ ${impact.linkedAssignmentCount} assignment ທີ່ເລືອກໂຊ ${showLabel} — ການລຶບຈະເອົາໂຊນີ້ອອກຈາກ roster ແລະ reprice assignments ທີ່ຍັງ unlock`
            : `ລຶບໂຊ ${showLabel}?`;

      if (impact.blockedAssignmentCount > 0) {
        await confirm({
          title: "ບໍ່ສາມາດລຶບໂຊ",
          description,
          actionText: "ປິດ",
          ActionProps: { variant: "outline" },
        });
        return;
      }

      const ok = await confirm({
        title: "ລຶບໂຊ",
        description,
        actionText: "ລຶບ",
        ActionProps: { variant: "destructive" },
      });
      if (!ok) return;

      await deleteShow.mutateAsync(showId);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ລຶບໂຊລົ້ມເຫຼວ",
      );
    }
  };

  return (
    <section className="mt-4 rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <CalendarDaysIcon className="size-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="font-semibold text-xs uppercase tracking-widest">
              ຕາຕະລາງໂຊ
            </p>
            <p className="text-muted-foreground text-xs">
              {summary.showCount > 0
                ? `${summary.showCount} ໂຊ · ${summary.calendarDayCount} ມື້`
                : "ຍັງບໍ່ມີໂຊ"}
            </p>
          </div>
          {summary.sameDayDiscountApplies ? (
            <Badge variant="outline">
              ໂຊທີ 2+ ມື້ດຽວກັນ → 60%
            </Badge>
          ) : null}
        </div>
        {canManage ? (
          <Button
            type="button"
            size="sm"
            onClick={() => setDialog({ open: true, mode: "create" })}
          >
            <PlusIcon className="size-4" />
            ເພີ່ມໂຊ
          </Button>
        ) : null}
      </div>

      {lastCreatedShowId && canManage ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/10 px-4 py-2">
          <p className="text-muted-foreground text-xs">
            ເພີ່ມໂຊໃໝ່ແລ້ວ — ຕ້ອງການເພີ່ມໃຫ້ນັກສະແດງທຸກຄົນບໍ?
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={submitting}
            onClick={() =>
              void addShowToAll
                .mutateAsync({ eventShowId: lastCreatedShowId })
                .then(() => setLastCreatedShowId(null))
            }
          >
            <UserPlusIcon className="size-4" />
            ເພີ່ມໂຊໃໝ່ໃຫ້ທຸກຄົນ
          </Button>
        </div>
      ) : null}

      <div className="px-4 py-3">
        {showsQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : shows.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ມີຕາຕະລາງໂຊ — ລະບົບຈະສ້າງ 1 ໂຊອັດຕະໂນມັດເມື່ອຈັດທີມ
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-xs">
                  <th className="px-2 py-2 text-left font-medium">#</th>
                  <th className="px-2 py-2 text-left font-medium">
                    ວັນ-ເວລາ
                  </th>
                  <th className="px-2 py-2 text-left font-medium">ໝາຍເຫດ</th>
                  {canManage ? (
                    <th className="px-2 py-2 text-right font-medium">ຈັດການ</th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y">
                {shows.map((show, index) => (
                  <tr key={show.id}>
                    <td className="px-2 py-2 text-muted-foreground tabular-nums">
                      {index + 1}
                    </td>
                    <td className="px-2 py-2 font-medium">
                      {formatDateTimeLocal(show.showDate)}
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">
                      {show.notes?.trim() ? show.notes : "—"}
                    </td>
                    {canManage ? (
                      <td className="px-2 py-2 text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setDialog({ open: true, mode: "edit", show })
                          }
                        >
                          <PencilIcon className="size-4" />
                          ແກ້ໄຂ
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EventShowDialog
        state={dialog}
        submitting={submitting}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDialog({ open: false });
        }}
        onSubmitCreate={async (input) => {
          const created = await createShow.mutateAsync(input);
          setLastCreatedShowId(created.id);
        }}
        onSubmitEdit={async (showId, input) => {
          await updateShow.mutateAsync({ showId, input });
        }}
        onDelete={
          canManage
            ? async (showId) => {
                await confirmDeleteShow(showId);
              }
            : undefined
        }
      />
    </section>
  );
}
